// src/pages/admin/components/HomepageRequests.js
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TableSortLabel, useMediaQuery, Snackbar, Alert, CircularProgress,
  IconButton, Collapse, TablePagination, Typography, TextField, InputAdornment,
  Tooltip, Chip, Select, MenuItem, FormControl, InputLabel, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Avatar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon      from '@mui/icons-material/ExpandMore';
import ExpandLessIcon      from '@mui/icons-material/ExpandLess';
import CloudDownloadIcon   from '@mui/icons-material/CloudDownload';
import MailOutlineIcon     from '@mui/icons-material/MailOutline';
import CheckIcon           from '@mui/icons-material/Check';
import SearchIcon          from '@mui/icons-material/Search';
import RefreshIcon         from '@mui/icons-material/Refresh';
import InfoIcon            from '@mui/icons-material/Info';
import OpenInNewIcon       from '@mui/icons-material/OpenInNew';
import goobusinessImage    from '../../../assets/goobusiness.PNG';

const FONT_SIZE = '0.875rem';
const API_URL   = process.env.REACT_APP_API_URL;

// ─── Sterne ──────────────────────────────────────────────────────────────────
const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 0 24 24" width="18" fill="#FFA726">
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);
const StarBorderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 0 24 24" width="18" fill="#FFA726">
    <path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
  </svg>
);

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────
const formatDateTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString)
    .toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    .replace(',', ' –');
};

const renderRatingStars = (rating) => {
  const value = parseFloat(rating);
  if (isNaN(value)) return null;
  const full  = Math.min(Math.round(value), 5);
  const empty = 5 - full;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
      {[...Array(full)].map((_, i)  => <StarIcon key={`f${i}`} />)}
      {[...Array(empty)].map((_, i) => <StarBorderIcon key={`e${i}`} />)}
      <Typography fontSize="0.75rem" ml={0.5} color="text.secondary">
        {typeof value === 'number' ? value.toFixed(1) : value}
      </Typography>
    </Box>
  );
};

// ─── Bewertungs-Detail-Dialog ─────────────────────────────────────────────────
const ReviewDetailDialog = ({ open, review, onClose }) => {
  if (!review) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1.5}>
          {review.reviewerPhotoUrl
            ? <Avatar src={review.reviewerPhotoUrl} sx={{ width: 36, height: 36 }} />
            : <Avatar sx={{ width: 36, height: 36, bgcolor: '#e0e0e0', fontSize: 14 }}>
                {review.name?.[0] || '?'}
              </Avatar>
          }
          <Box>
            <Typography fontWeight="bold" fontSize="0.95rem">{review.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(review.publishedAtDate)}
            </Typography>
          </Box>
          <Box ml="auto">{renderRatingStars(review.stars || review.rating)}</Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {review.text && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>Bewertungstext</Typography>
            <Typography variant="body2">{review.text}</Typography>
          </Box>
        )}
        {review.responseFromOwnerText && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>Antwort vom Inhaber</Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              {formatDateTime(review.responseFromOwnerDate)}
            </Typography>
            <Typography variant="body2">{review.responseFromOwnerText}</Typography>
          </Box>
        )}
        {review.reviewImageUrls?.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>Bilder</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {review.reviewImageUrls.map((url, i) => (
                <img key={i} src={url} alt={`Bild ${i + 1}`} width={70} style={{ borderRadius: 4 }} />
              ))}
            </Box>
          </Box>
        )}
        {review.reviewContext && Object.keys(review.reviewContext).length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>Kontext</Typography>
            {Object.entries(review.reviewContext).map(([k, v]) => (
              <Typography key={k} variant="body2"><b>{k}:</b> {v}</Typography>
            ))}
          </Box>
        )}
        {review.reviewDetailedRating && Object.keys(review.reviewDetailedRating).length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Detailwertung</Typography>
            {Object.entries(review.reviewDetailedRating).map(([k, v]) => (
              <Typography key={k} variant="body2"><b>{k}:</b> {v}</Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
const HomepageRequests = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows,           setRows]           = useState([]);
  const [searchTerm,     setSearchTerm]     = useState('');
  const [emailFilter,    setEmailFilter]    = useState('all');   // 'all' | 'sent' | 'unsent'
  const [tokenFilter,    setTokenFilter]    = useState('all');   // 'all' | 'opened' | 'unopened'
  const [orderBy,        setOrderBy]        = useState('erstellungsdatum');
  const [order,          setOrder]          = useState('desc');
  const [page,           setPage]           = useState(0);
  const [rowsPerPage,    setRowsPerPage]    = useState(25);
  const [loading,        setLoading]        = useState(false);
  const [snackbar,       setSnackbar]       = useState({ open: false, message: '', severity: 'success' });
  const [openRows,       setOpenRows]       = useState({});
  const [reviewsLoading, setReviewsLoading] = useState({});
  const [scraping,       setScraping]       = useState({});
  const [scrapedData,    setScrapedData]    = useState({});
  const [sending,        setSending]        = useState({});
  const [detailDialog,   setDetailDialog]   = useState({ open: false, review: null });
  const [lastRefresh,    setLastRefresh]    = useState(null);

  // Auto-Polling Ref
  const pollingRef = useRef(null);

  // ── getHeaders mit useCallback (Punkt 12) ──
  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // ── Daten laden ──
  const fetchAngebote = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/angebote`, { headers: getHeaders() });
      setRows(data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error(err);
      showSnackbar('Fehler beim Abrufen der Daten.', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getHeaders, showSnackbar]);

  useEffect(() => {
    fetchAngebote();
    // Auto-Polling alle 60 Sekunden (Punkt 1)
    pollingRef.current = setInterval(() => fetchAngebote(true), 60000);
    return () => clearInterval(pollingRef.current);
  }, [fetchAngebote]);

  // ── Bewertungen laden (Punkt 2: zuverlässig neu laden) ──
  const loadReviewsForAngebot = useCallback(async (id) => {
    setReviewsLoading(prev => ({ ...prev, [id]: true }));
    try {
      const { data } = await axios.get(`${API_URL}/api/screviews/${id}`, { headers: getHeaders() });
      setScrapedData(prev => ({ ...prev, [id]: data }));
    } catch (err) {
      console.error(`Fehler beim Laden der Bewertungen für ID ${id}:`, err);
      setScrapedData(prev => ({ ...prev, [id]: [] }));
    } finally {
      setReviewsLoading(prev => ({ ...prev, [id]: false }));
    }
  }, [getHeaders]);

  // ── Filtern & Suchen (Punkt 3: alle Felder) ──
  const filteredRows = useMemo(() => {
    // Duplikate entfernen
    const seen = new Set();
    const unique = rows.filter(r => {
      if (seen.has(r.id)) return false;
      seen.add(r.id);
      return true;
    });

    return unique.filter(r => {
      // Suche über alle relevanten Felder (Punkt 3)
      const text = [
        r.id, r.firmenname, r.email, r.telefonnummer,
        r.gutscheincode, r.adresse, r.name,
      ].join(' ').toLowerCase();
      if (searchTerm && !text.includes(searchTerm.toLowerCase())) return false;

      // E-Mail-Filter (Punkt 9)
      if (emailFilter === 'sent'   && !r.email_versendet) return false;
      if (emailFilter === 'unsent' &&  r.email_versendet) return false;

      // Token-Filter (Punkt 9)
      if (tokenFilter === 'opened'   && !r.used)       return false;
      if (tokenFilter === 'unopened' &&  r.used)        return false;

      return true;
    });
  }, [rows, searchTerm, emailFilter, tokenFilter]);

  // ── Sortieren ──
  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const valA = orderBy === 'erstellungsdatum' ? new Date(a[orderBy]) : (a[orderBy]?.toString() || '');
      const valB = orderBy === 'erstellungsdatum' ? new Date(b[orderBy]) : (b[orderBy]?.toString() || '');
      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRows, orderBy, order]);

  const paginatedRows = useMemo(() => {
    return sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  // ── KPI-Zahlen für Zusammenfassung (Punkt 10) ──
  const kpis = useMemo(() => {
    const unique = [...new Map(rows.map(r => [r.id, r])).values()];
    return {
      gesamt:       unique.length,
      ohneEmail:    unique.filter(r => !r.email_versendet).length,
      ohneScrape:   unique.filter(r => !r.last_scraped_at).length,
      tokenGeoeffnet: unique.filter(r => r.used).length,
    };
  }, [rows]);

  // ── Handlers ──
  const handleRequestSort = useCallback(prop => {
    setOrder(prev => orderBy === prop && prev === 'asc' ? 'desc' : 'asc');
    setOrderBy(prop);
  }, [orderBy]);

  const handleChangePage          = useCallback((_, p) => setPage(p), []);
  const handleChangeRowsPerPage   = useCallback(e => { setRowsPerPage(+e.target.value); setPage(0); }, []);

  const handleToggleRow = useCallback(async (id) => {
    setOpenRows(prev => {
      const next = !prev[id];
      if (next && scrapedData[id] === undefined) {
        loadReviewsForAngebot(id);
      }
      return { ...prev, [id]: next };
    });
  }, [scrapedData, loadReviewsForAngebot]);

  // ── Scrapen (Punkte 2, 5, 11) ──
  const handleScrapeReviews = useCallback(async (id) => {
    const selected = rows.find(r => r.id === id);
    if (!selected) { showSnackbar('Eintrag nicht gefunden.', 'error'); return; }

    setScraping(prev => ({ ...prev, [id]: true }));
    try {
      const { data } = await axios.post(
        `${API_URL}/api/scrape-and-save-reviews`,
        { angebot_id: selected.id, place_id: selected.place_id },
        { headers: getHeaders() }
      );

      if (!data.success) throw new Error(data.message || 'Unbekannter Fehler.');

      // Bewertungen zuverlässig neu laden (Punkt 2)
      await loadReviewsForAngebot(id);

      // Bild aktualisieren
      if (data.imageUrl?.includes('googleusercontent')) {
        setRows(prev => prev.map(r => r.id === id ? { ...r, bild_url: data.imageUrl } : r));
      }

      // Timestamp aktualisieren (Punkt 5: Erfolgs-Snackbar)
      setRows(prev => prev.map(r => r.id === id ? { ...r, last_scraped_at: new Date().toISOString() } : r));
      showSnackbar(`${data.count ?? ''} Bewertungen erfolgreich geladen.`, 'success');
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      showSnackbar(`Fehler beim Scrapen: ${msg}`, 'error');
    } finally {
      setScraping(prev => ({ ...prev, [id]: false }));
    }
  }, [rows, getHeaders, loadReviewsForAngebot, showSnackbar]);

  // ── E-Mail senden (Punkt 5: Erfolgs-Snackbar) ──
  const handleSendEmail = useCallback(async (id) => {
    setSending(prev => ({ ...prev, [id]: true }));
    try {
      const { data } = await axios.post(
        `${API_URL}/api/send-email`,
        { id },
        { headers: { 'Content-Type': 'application/json', ...getHeaders() } }
      );
      if (!data.success) throw new Error(data.message || 'Unbekannt');
      setRows(prev => prev.map(r =>
        r.id === id ? { ...r, email_versendet: 1, email_versanddatum: new Date().toISOString() } : r
      ));
      showSnackbar('E-Mail erfolgreich versendet.', 'success');
    } catch (err) {
      const msg = err.response?.data?.message ?? err.message;
      showSnackbar(`Fehler beim Senden der E-Mail: ${msg}`, 'error');
    } finally {
      setSending(prev => ({ ...prev, [id]: false }));
    }
  }, [getHeaders, showSnackbar]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box p={{ xs: 1, sm: 3 }}>

      {/* ── KPI-Zusammenfassung (Punkt 10) ── */}
      {!loading && (
        <Box display="flex" flexWrap="wrap" gap={1.5} mb={2}>
          {[
            { label: 'Anfragen gesamt',    value: kpis.gesamt,         color: '#1976d2' },
            { label: 'Ohne E-Mail',        value: kpis.ohneEmail,      color: '#ed6c02' },
            { label: 'Ohne Scrape',        value: kpis.ohneScrape,     color: '#9c27b0' },
            { label: 'Token geöffnet',     value: kpis.tokenGeoeffnet, color: '#2e7d32' },
          ].map(kpi => (
            <Box
              key={kpi.label}
              sx={{
                bgcolor: 'white', border: '1px solid #e0e0e0',
                borderRadius: 2, px: 2, py: 1, minWidth: 120, textAlign: 'center',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              <Typography fontWeight="bold" fontSize="1.35rem" sx={{ color: kpi.color }}>
                {kpi.value}
              </Typography>
              <Typography fontSize="0.72rem" color="text.secondary">{kpi.label}</Typography>
            </Box>
          ))}
          {lastRefresh && (
            <Box sx={{ ml: 'auto', alignSelf: 'center' }}>
              <Typography fontSize="0.72rem" color="text.disabled">
                Zuletzt aktualisiert: {formatDateTime(lastRefresh)}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ── Toolbar: Suche + Filter + Refresh (Punkte 1, 3, 9) ── */}
      <Box
        mb={2}
        display="flex"
        flexWrap="wrap"
        gap={1.5}
        alignItems="center"
      >
        <TextField
          size="small"
          placeholder="Suchen… (Name, E-Mail, Tel., Gutschein, Adresse)"
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); setPage(0); }}
          sx={{ minWidth: 280, flexGrow: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />

        {/* E-Mail-Filter (Punkt 9) */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>E-Mail Status</InputLabel>
          <Select value={emailFilter} label="E-Mail Status" onChange={e => { setEmailFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value="sent">Gesendet</MenuItem>
            <MenuItem value="unsent">Nicht gesendet</MenuItem>
          </Select>
        </FormControl>

        {/* Token-Filter (Punkt 9) */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Token Status</InputLabel>
          <Select value={tokenFilter} label="Token Status" onChange={e => { setTokenFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value="opened">Token geöffnet</MenuItem>
            <MenuItem value="unopened">Token nicht geöffnet</MenuItem>
          </Select>
        </FormControl>

        {/* Refresh-Button (Punkt 1) */}
        <Tooltip title="Daten aktualisieren">
          <span>
            <IconButton onClick={() => fetchAngebote()} disabled={loading} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* ── Tabelle ── */}
      {loading ? (
        <Box display="flex" justifyContent="center" minHeight="400px" alignItems="center">
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={2} sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: '68vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: FONT_SIZE, width: 40 }} />
                  <TableCell sx={{ fontSize: FONT_SIZE, width: 50 }}>ID</TableCell>
                  <TableCell sx={{ fontSize: FONT_SIZE }}>Unternehmen</TableCell>
                  <TableCell sx={{ fontSize: FONT_SIZE }}>E-Mail</TableCell>
                  {!isMobile && <TableCell sx={{ fontSize: FONT_SIZE }}>Telefon</TableCell>}
                  <TableCell sx={{ fontSize: FONT_SIZE }}>
                    <TableSortLabel
                      active={orderBy === 'erstellungsdatum'}
                      direction={orderBy === 'erstellungsdatum' ? order : 'asc'}
                      onClick={() => handleRequestSort('erstellungsdatum')}
                      sx={{ fontSize: FONT_SIZE }}
                    >
                      Datum
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontSize: FONT_SIZE }}>Bewertung</TableCell>
                  {/* Punkt 8: Gutscheincode aus Hauptspalten entfernt */}
                  <TableCell sx={{ fontSize: FONT_SIZE }}>E-Mail</TableCell>
                  {/* Punkt 6: Token-Status Spalte */}
                  <TableCell sx={{ fontSize: FONT_SIZE }}>Token</TableCell>
                  <TableCell sx={{ fontSize: FONT_SIZE }}>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.map(row => (
                  <React.Fragment key={row.id}>
                    <TableRow hover sx={{ '& > *': { borderBottom: openRows[row.id] ? 'none' : undefined } }}>

                      {/* Expand */}
                      <TableCell>
                        <IconButton size="small" onClick={() => handleToggleRow(row.id)}>
                          {openRows[row.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>

                      {/* ID */}
                      <TableCell sx={{ fontSize: FONT_SIZE, color: 'text.secondary' }}>{row.id}</TableCell>

                      {/* Unternehmen */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <a href={row.business_url} target="_blank" rel="noopener noreferrer">
                            <img
                              src={row.bild_url?.includes('googleusercontent') ? row.bild_url : goobusinessImage}
                              alt={row.firmenname}
                              width={36}
                              style={{ borderRadius: 4, display: 'block', flexShrink: 0 }}
                            />
                          </a>
                          <Box>
                            <Typography fontSize={FONT_SIZE} fontWeight="bold" noWrap sx={{ maxWidth: 180 }}>
                              {row.firmenname?.split(',')[0]}
                            </Typography>
                            <Typography fontSize="0.75rem" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
                              {row.adresse}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* E-Mail */}
                      <TableCell sx={{ fontSize: FONT_SIZE }}>{row.email}</TableCell>

                      {/* Telefon */}
                      {!isMobile && (
                        <TableCell sx={{ fontSize: FONT_SIZE, color: 'text.secondary' }}>
                          {row.telefonnummer || '–'}
                        </TableCell>
                      )}

                      {/* Datum */}
                      <TableCell sx={{ fontSize: FONT_SIZE, whiteSpace: 'nowrap' }}>
                        {formatDateTime(row.erstellungsdatum)}
                      </TableCell>

                      {/* Bewertung */}
                      <TableCell>
                        {renderRatingStars(row.bewertung)}
                        <Typography fontSize="0.72rem" color="text.secondary">
                          {row.bewertungen_gesamt ? `(${row.bewertungen_gesamt})` : ''}
                        </Typography>
                      </TableCell>

                      {/* E-Mail Status – Chip statt langer String (Punkt 4) */}
                      <TableCell>
                        <Tooltip title={row.email_versendet ? `Gesendet: ${formatDateTime(row.email_versanddatum)}` : 'Noch nicht gesendet'}>
                          <Chip
                            label={row.email_versendet ? 'Gesendet' : 'Ausstehend'}
                            size="small"
                            color={row.email_versendet ? 'success' : 'default'}
                            variant={row.email_versendet ? 'filled' : 'outlined'}
                            sx={{ fontSize: '0.72rem' }}
                          />
                        </Tooltip>
                      </TableCell>

                      {/* Token-Status (Punkt 6) */}
                      <TableCell>
                        {row.link_token ? (
                          <Tooltip title={row.used ? `Geöffnet: ${formatDateTime(row.used_at)}` : 'Noch nicht geöffnet'}>
                            <Chip
                              label={row.used ? 'Geöffnet' : 'Nicht geöffnet'}
                              size="small"
                              color={row.used ? 'primary' : 'default'}
                              variant={row.used ? 'filled' : 'outlined'}
                              sx={{ fontSize: '0.72rem' }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography fontSize="0.72rem" color="text.disabled">Kein Link</Typography>
                        )}
                      </TableCell>

                      {/* Aktionen */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Tooltip title={row.email_versendet ? 'E-Mail erneut senden (verlängert Token-Link)' : 'E-Mail senden'}>
                            <span>
                              <IconButton onClick={() => handleSendEmail(row.id)} disabled={!!sending[row.id]} size="small">
                                {sending[row.id]
                                  ? <CircularProgress size={14} />
                                  : row.email_versendet
                                  ? <CheckIcon fontSize="small" color="success" />
                                  : <MailOutlineIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Bewertungen laden (via n8n)">
                            <span>
                              <IconButton onClick={() => handleScrapeReviews(row.id)} disabled={!!scraping[row.id]} size="small">
                                {scraping[row.id] ? <CircularProgress size={14} /> : <CloudDownloadIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                        {row.last_scraped_at && (
                          <Typography fontSize="0.7rem" color="text.disabled" noWrap>
                            Scrape: {formatDateTime(row.last_scraped_at)}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* ── Aufgeklappte Bewertungszeile (Punkt 7: kompakter mit Detail-Dialog) ── */}
                    <TableRow>
                      <TableCell padding="none" colSpan={isMobile ? 9 : 10} sx={{ bgcolor: '#fafafa' }}>
                        <Collapse in={!!openRows[row.id]} timeout="auto" unmountOnExit>
                          <Box p={2}>

                            {/* Gutscheincode hier eingebettet (Punkt 8) */}
                            <Box display="flex" flexWrap="wrap" gap={2} mb={1.5} alignItems="center">
                              <Typography fontSize={FONT_SIZE} fontWeight="bold">
                                {row.firmenname?.split(',')[0]}
                              </Typography>
                              {row.gutscheincode && (
                                <Chip label={`Gutschein: ${row.gutscheincode}`} size="small" variant="outlined" color="secondary" />
                              )}
                              {row.link_token && (
                                <Tooltip title="Token-Link öffnen">
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={`https://adm.sternefix.de/angebote/${row.link_token}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>

                            <Divider sx={{ mb: 1.5 }} />

                            {reviewsLoading[row.id] ? (
                              <Box display="flex" alignItems="center" gap={1} py={2}>
                                <CircularProgress size={20} />
                                <Typography fontSize={FONT_SIZE}>Bewertungen werden geladen…</Typography>
                              </Box>
                            ) : scrapedData[row.id]?.length > 0 ? (
                              <>
                                <Typography fontSize={FONT_SIZE} fontWeight="bold" mb={1}>
                                  {scrapedData[row.id].length} Bewertung(en)
                                </Typography>
                                {/* Kompakte Karten statt breiter Tabelle (Punkt 7) */}
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: 1.5,
                                    maxHeight: 340,
                                    overflowY: 'auto',
                                    pr: 0.5,
                                  }}
                                >
                                  {scrapedData[row.id].map((rev, i) => (
                                    <Box
                                      key={i}
                                      sx={{
                                        bgcolor: 'white',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 2,
                                        p: 1.5,
                                        position: 'relative',
                                      }}
                                    >
                                      {/* Kopfzeile */}
                                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                        {rev.reviewerPhotoUrl
                                          ? <img src={rev.reviewerPhotoUrl} alt={rev.name} width={28} style={{ borderRadius: '50%', flexShrink: 0 }} />
                                          : <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: '#e0e0e0' }}>{rev.name?.[0] || '?'}</Avatar>
                                        }
                                        <Box flexGrow={1} minWidth={0}>
                                          <Typography fontSize="0.8rem" fontWeight="bold" noWrap>{rev.name}</Typography>
                                          <Typography fontSize="0.7rem" color="text.secondary">{formatDateTime(rev.publishedAtDate)}</Typography>
                                        </Box>
                                        <Box flexShrink={0}>{renderRatingStars(rev.stars || rev.rating)}</Box>
                                      </Box>

                                      {/* Text */}
                                      {rev.text && (
                                        <Typography
                                          fontSize="0.78rem"
                                          color="text.secondary"
                                          sx={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            mb: 0.5,
                                          }}
                                        >
                                          {rev.text}
                                        </Typography>
                                      )}

                                      {/* Details-Button */}
                                      <Box display="flex" justifyContent="flex-end">
                                        <Tooltip title="Details anzeigen">
                                          <IconButton
                                            size="small"
                                            onClick={() => setDetailDialog({ open: true, review: rev })}
                                          >
                                            <InfoIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              </>
                            ) : (
                              <Typography fontSize={FONT_SIZE} color="text.secondary">
                                Keine Bewertungen vorhanden. Bitte zuerst scrapen.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>

                  </React.Fragment>
                ))}

                {paginatedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 9 : 10} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">Keine Einträge gefunden.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={sortedRows.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={<span style={{ fontSize: FONT_SIZE }}>Zeilen:</span>}
            labelDisplayedRows={({ from, to, count }) => (
              <span style={{ fontSize: FONT_SIZE }}>{from}–{to} von {count}</span>
            )}
          />
        </Paper>
      )}

      {/* ── Detail-Dialog (Punkt 7) ── */}
      <ReviewDetailDialog
        open={detailDialog.open}
        review={detailDialog.review}
        onClose={() => setDetailDialog({ open: false, review: null })}
      />

      {/* ── Snackbar (Punkte 5, Erfolg + Fehler) ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HomepageRequests;